import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import type { AuthUser, Role } from '@embrace-returns/shared';
import { OIDC_PROVIDER } from '../amplify';

type Status = 'loading' | 'authed' | 'anon';

interface AuthState {
  status: Status;
  user: AuthUser | null;
  /** Optional Cognito IdP name to pin (e.g. 'Entra' | 'EntraEmbrace'). */
  signIn: (provider?: string) => void;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

const KNOWN_ROLES: Role[] = ['embrace-user', 'embrace-manager', 'sl-admin'];

async function readUser(): Promise<AuthUser | null> {
  const session = await fetchAuthSession();
  const claims = session.tokens?.idToken?.payload;
  if (!claims?.email) return null;

  const email = String(claims.email).toLowerCase();
  const rawRole = claims['custom:role'] as string | undefined;
  const role: Role = KNOWN_ROLES.includes(rawRole as Role)
    ? (rawRole as Role)
    : 'embrace-user';
  const name =
    (claims.name as string) ??
    (claims.given_name as string) ??
    email.split('@')[0];

  return { email, name, role };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await readUser();
      setUser(u);
      setStatus(u ? 'authed' : 'anon');
    } catch {
      setUser(null);
      setStatus('anon');
    }
  }, []);

  useEffect(() => {
    refresh();
    const stop = Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'signedIn' ||
        payload.event === 'signInWithRedirect' ||
        payload.event === 'signedOut'
      ) {
        refresh();
      }
    });
    return stop;
  }, [refresh]);

  const signIn = useCallback((provider?: string) => {
    void signInWithRedirect({ provider: { custom: provider ?? OIDC_PROVIDER } });
  }, []);

  const signOut = useCallback(() => {
    void amplifySignOut();
  }, []);

  return (
    <Ctx.Provider value={{ status, user, signIn, signOut, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export const isManager = (u: AuthUser | null) =>
  u?.role === 'embrace-manager' || u?.role === 'sl-admin';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center text-[13px] text-muted">
        Loading…
      </div>
    );
  }
  if (status === 'anon') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
